/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState, useContext } from 'react';
import MaskedInput from 'react-text-mask';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import { RecipientBookContext } from '../../../RecipientBook/Common/Context/RecipientBookContext';

// MUI
import {
    TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';

// Helpers
import { unmaskPhoneNumber } from './Helpers';
import { isEmpty } from '../../../../../utils/object';
import { getFeatureFlag } from '../../../../../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import useFetchAPI from '../../../../../common/customHooks/useFetchAPI';
import validateMobileNumber from '../../../../../../../../apis/account-apis/validateMobileNumber';
/* eslint-disable react/jsx-props-no-spreading */

const useStyles = () => makeStyles(() => ({
    suggestedAddress: {
        top: '-20px',
        left: '4px',
        width: 'calc(100vw - 48px - 0)',
        outline: 'none',
        zIndex: 2,
        position: 'absolute',
        maxHeight: '300px',
        overflowY: 'scroll',
        backgroundColor: '#fff',
        '-webkit-box-shadow': '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
    errorText: {
        fontSize: '12px', 
        color: 'red',
        marginLeft: 0,
    }
}));

const TextFieldHelper = ({
    fieldName, optionsList, fieldLabel, required, methods, address, searchResults, handleSelectAnAddress, setShoultMakeApiCall, previousCountry = '', variant="filled", style, parentComponent,
}) => {

    const { showSuggestedAddressDropdown, setShowSuggestedAddressDropdown } = useContext(RecipientBookContext);

    const {
        formState: { errors },
        clearErrors
    } = methods;

    const classesLayout = useStyles();
    const classes = classesLayout();
    const addressInputRef = useRef(null);
    const [debounce, setDebounce] = useState('');
    const isFieldPhoneNumber = ["phoneNumber", "rcpPhone"].includes(fieldName);

    const isMobileNumberValidatorOn = useSelector(getFeatureFlag('is-mobile-number-validator-on'));
    const onError = (errorMessage) => methods.setError(fieldName, { type: 'custom', message: errorMessage || 'Phone number is invalid.' });
    const onSuccess = (isPhoneNumberValid) => !isPhoneNumberValid ? onError() : clearErrors(fieldName);

    const processValidatePhoneNumber = useFetchAPI(onSuccess, onError, validateMobileNumber, true);
    
    const showSuggestions = () => {
        if (!isEmpty(searchResults)) {
            return (
                <div style={{ position: 'relative' }}>
                    <div className={classes.suggestedAddress}>{
                        searchResults.map((address) => (
                            <MenuItem
                                key={address.suggestion}
                                onMouseDown={() => handleSelectAnAddress(address)}
                            >
                                {address.suggestion}
                            </MenuItem>
                        ))
                    }
                    </div>
                </div>
            );
        }
        return null;
    };

    const handleOnBlur = () => {
        // If the blur event is not caused by clicking on a suggestion, hide the suggestions
        if (!addressInputRef.current.contains(document.activeElement)) {
            if (typeof setShowSuggestedAddressDropdown === 'function') {
                setShowSuggestedAddressDropdown(false);
            }
        }
    };

    const handleOnChange = ({ target }) => {
        if (fieldName === 'addressType') {
            clearErrors(['employerName', 'addressLineOne', 'addressLineTwo']);
        } else if (fieldName === 'country' && previousCountry !== '') {
            const address = {
                addressLineOne: '',
                addressLineTwo: '',
                city: '',
                state: '',
                zip: '',
            };
            Object.keys(address).map((key) => methods.setValue(key, address[key]));
            clearErrors(['country', 'city', 'state', 'addressLineOne', 'addressLineTwo', 'addressType', 'zip'])
        } else if (fieldName === "zip" && methods?.getValues("country")?.toLowerCase()?.includes("ca")) {
            let formattedZipcode = methods.getValues("zip").toUpperCase().replace(' ', '');
            formattedZipcode = formattedZipcode.length === 6 ? `${formattedZipcode.substr(0, 3)} ${formattedZipcode.substr(3)}` : formattedZipcode;
            methods.setValue("zip", formattedZipcode);

        /* NOTE: pareComponent props needs to be cleaned up */
        } else if (isFieldPhoneNumber && isMobileNumberValidatorOn && (methods.getValues('rcpPhoneType') === "mobile" || parentComponent === "communicationCenter")) {
            const unmaskedPhoneNumber = unmaskPhoneNumber(target.value);

            // validating previous rcpPhoneType will make sure if the number is not bypassing the validation
            if (unmaskedPhoneNumber?.length === 10 && (unmaskPhoneNumber(address?.[fieldName]) !== unmaskedPhoneNumber || address?.rcpPhoneType === "landline")) {
                clearTimeout(debounce);
                setTimeout(onError, 0, 'Validating Phone Number.');
                setDebounce(setTimeout(() => processValidatePhoneNumber.mutate({ mobileNumber: unmaskedPhoneNumber, mobileNumberType: "mobile" }), 1000));
            }
        }
    };

    // Set default country to United States when user is adding a new address
    if (fieldName === 'country' && !address?.country) address.country = 'United States'
    // Support 'US' country value from BE
    // Old address book and checkout use 'US' - recip book docs show 'United States'

    // Support 'CN / CAN / CA' country value from BE
    // Old address book uses 'CA',  checkout uses 'CAN' or 'CA' - recip book docs show 'United States'

    // Per Antony, we should always send CA

    if (fieldName === 'country' && ('US', 'USA').includes(address?.[fieldName])) address.country = 'United States';
    if (fieldName === 'country' && ('CN', 'CAN', 'CA').includes(address?.[fieldName])) address.country = 'Canada';

    const inputRef = React.useRef(null);
    const handleFocus = (e) => {
        const fieldName = e.target?.name;
        // Only show QAS suggestions when the focused field name is addressLineOne
        if (fieldName === 'addressLineOne') {
            setShowSuggestedAddressDropdown(true);
        } else {
            setShowSuggestedAddressDropdown(false);
        }
    };

    if (optionsList) {
        return (
            <Controller
                name={fieldName}
                control={methods.control}
                defaultValue={(fieldName === 'suggestedAddress' || fieldName === 'addressType') ? optionsList[0] : (address?.[fieldName] || '')}
                render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                        <InputLabel required={required} id={`${fieldName}-label`}>{fieldLabel}</InputLabel>
                        <Select
                            className={classes.frequencySelect}
                            {...field}
                            onChange={(e) => { field.onChange(e); handleOnChange(e) }}
                            labelId={`${fieldName}-label`}
                            name={fieldName}
                            id={fieldName}
                            variant={variant}
                            label={fieldLabel}
                            error={errors?.[fieldName]?.message}
                            style={{ marginBottom: errors?.[fieldName]?.message ? '0px' : '20px' }}
                            native
                        >
                            <option value="" disabled></option>
                            {optionsList.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </Select>
                        {errors?.[fieldName]?.message ? (
                            <FormHelperText className={classes.errorText}>
                                {errors?.[fieldName]?.message}
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                )}
            />
        );
    }

    return (
        <Controller
            name={fieldName}
            control={methods.control}
            defaultValue={
                (isFieldPhoneNumber && address?.[fieldName])
                    ? address[fieldName].replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
                    : (address?.[fieldName]) || ''
            }
            render={({ field }) => (
                <FormControl variant="filled" fullWidth>
                    <TextField
                        {...field}
                        ref={(inputRef) => {
                            field.ref(inputRef);
                            addressInputRef.current = inputRef;
                        }}
                        id={fieldName}
                        type="text"
                        variant={variant}
                        onChange={(e) => { field.onChange(e); handleOnChange(e) }}
                        placeholder={isFieldPhoneNumber ? '(_ _ _) _ _ _ - _ _ _ _' : ''}
                        fullWidth
                        label={fieldLabel}
                        disabled={processValidatePhoneNumber?.isLoading || false}
                        required={required}
                        error={errors?.[fieldName]?.message}
                        style={style || { marginBottom: errors?.[fieldName]?.message ? '0px' : '20px' }}
                        InputProps={isFieldPhoneNumber ? {
                            inputComponent: MaskedInput,
                            inputProps: {
                                mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/,],
                                guide: true,
                            },
                            ref: inputRef,
                        } : {}}
                        InputLabelProps={isFieldPhoneNumber ? {
                            shrink: true,
                            } : {}
                        }
                        onKeyUp={fieldName === 'addressLineOne' ? (() => setShoultMakeApiCall(true)) : () => { }}
                        onBlur={handleOnBlur}
                        onFocus={(e) => { 
                            e.persist();
                            handleFocus(e);
                        }}
                    />
                    {(searchResults && showSuggestedAddressDropdown) && showSuggestions()}
                    {errors?.[fieldName]?.message ? (
                        <FormHelperText className={classes.errorText}>
                            {errors?.[fieldName]?.message}
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
        />
    );
};


export default TextFieldHelper;
